import { useId } from 'react'

import { api, unwrap } from '../api/client'
import { useSealedQuery } from '../api/sealed'
import type { ModuleNode } from '../api/types'
import { useSessionEra } from '../documents/held'
import { t } from '../i18n'
import { ErrorNotice, Overlay, Pending } from '../ui'
import { weekStart } from './weekStart'

/**
 * What the board is decided by, opened from the "here" under the leaderboard.
 *
 * Two halves, and both are the ones scoring actually applies. The points per
 * action are the instance rule set (`/points/weights`), which is what every
 * ledger entry is priced from. Who earns and who ranks is the project's role
 * participation (`/board/rules` roles), which eligibility applies to every row.
 *
 * **What is not shown, on purpose.** `/board/rules` also returns the project's
 * stored board settings (weights, caps, window and the rest). Nothing in the
 * scoring path reads them -- the derivation from the `board_config` table out
 * finds only this rules read, the write and replication -- so listing them as
 * "this board's rules" would state a rule nobody is scored under.
 */
type Weight = {
  module: string
  verb: string
  base: number
  settles_after_days?: number | null
  pending?: number | null
  pending_week?: string | null
}

/** `responded@v1` → `Responded`, `rfi_closed@v1` → `Rfi closed`. */
export function actionLabel(verb: string): string {
  const words = verb
    .replace(/@v\d+$/, '')
    .replace(/_/g, ' ')
    .trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function BoardRules({
  projectId,
  modules,
  onClose,
}: {
  projectId: string
  modules: readonly ModuleNode[]
  onClose: () => void
}) {
  const titleId = useId()

  // Read under this session and sealed on a refusal, so a refused read takes
  // the tables away rather than leaving the last answer beside the refusal,
  // and the next person at this browser does not see the last one's.
  const era = useSessionEra()
  const scope = `engage/rules/${projectId}`
  const { query: weights, data: weightRows } = useSealedQuery({
    // This project's week: a price changes at Monday on the project's own
    // calendar, so "this week" here is the week its points are priced by.
    key: ['engage', 'weights', projectId],
    era,
    scope,
    read: async () =>
      unwrap(
        await api.GET('/engage/points/weights', {
          params: { query: { project_id: projectId } },
        }),
      ) as Weight[],
  })
  const { query: rules, data: rulesData } = useSealedQuery({
    key: ['engage', 'board-rules', projectId],
    era,
    scope,
    read: async () =>
      unwrap(
        await api.GET('/engage/board/rules', {
          params: { query: { project_id: projectId } },
        }),
      ),
  })

  const moduleName = (code: string): string =>
    modules.find((module) => module.route_prefix === `/${code}` || module.tool_code === code)
      ?.name ?? code

  // Grouped by module and in the module's own order, so a crew finds its own
  // work under one heading rather than scattered through a flat list.
  const grouped = new Map<string, Weight[]>()
  for (const weight of weightRows ?? []) {
    const rows = grouped.get(weight.module) ?? []
    rows.push(weight)
    grouped.set(weight.module, rows)
  }

  const roles = rulesData?.roles ?? []

  return (
    <Overlay kind="dialog" titleId={titleId} onClose={onClose}>
      <header>
        <h2 id={titleId}>{t('engage.rules.title')}</h2>
        <button type="button" className="subtle" onClick={onClose}>
          {t('app.close')}
        </button>
      </header>
      <div className="body">
        <h3>{t('engage.rules.points')}</h3>
        <p className="muted">{t('engage.rules.points_explain')}</p>
        {weights.isPending ? <Pending /> : null}
        {weights.error ? (
          <ErrorNotice error={weights.error} retry={() => void weights.refetch()} />
        ) : null}
        {weightRows ? (
          <table className="board-rules">
            <thead>
              <tr>
                <th scope="col">{t('engage.rules.action')}</th>
                <th scope="col" className="num">
                  {t('engage.rules.points_column')}
                </th>
                <th scope="col" className="num">
                  {t('engage.rules.settles')}
                </th>
              </tr>
            </thead>
            {[...grouped.entries()].map(([module, rows]) => (
              <tbody key={module}>
                <tr>
                  <th scope="rowgroup" colSpan={3}>
                    {moduleName(module)}
                  </th>
                </tr>
                {rows.map((row) => (
                  <tr key={`${row.module}:${row.verb}`}>
                    <td>{actionLabel(row.verb)}</td>
                    <td className="num">
                      {row.base}
                      {row.pending != null && row.pending_week ? (
                        <div className="muted">
                          {t('engage.rules.coming', {
                            points: row.pending,
                            from: weekStart(row.pending_week),
                          })}
                        </div>
                      ) : null}
                    </td>
                    <td className="num">
                      {row.settles_after_days == null
                        ? '—'
                        : t('engage.rules.days', {
                            days: row.settles_after_days,
                          })}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        ) : null}

        <h3>{t('engage.rules.who')}</h3>
        {rules.isPending ? <Pending /> : null}
        {rules.error ? (
          <ErrorNotice error={rules.error} retry={() => void rules.refetch()} />
        ) : null}
        {rulesData && roles.length === 0 ? (
          <p className="muted">{t('engage.rules.who_default')}</p>
        ) : null}
        {roles.length > 0 ? (
          <ul className="board-roles">
            {roles.map((role) => (
              <li key={role.template_id}>
                <strong>{role.template_name ?? '—'}</strong>
                {' · '}
                {role.ranks
                  ? t('engage.rules.ranks')
                  : role.earns
                    ? t('engage.rules.earns_only')
                    : t('engage.rules.neither')}
              </li>
            ))}
          </ul>
        ) : null}
        {/* Silence means in: a role with no row earns and ranks (eligibility.py). */}
        {roles.length > 0 ? <p className="muted">{t('engage.rules.who_others')}</p> : null}
      </div>
    </Overlay>
  )
}
